/**
 * tRPC-merging hook for the order-history list screen: fetches one page of
 * orders.listByStore and shapes it into ready-to-render rows via
 * orderHistoryViewModel's pure helpers.
 *
 * Pagination (the offset) is owned by the caller (OrderHistoryPageInner), not
 * by this hook — mirrors useWeeklyProposal, which is a pure query+merge and
 * leaves interaction state (edit state there, pagination here) to the page.
 * That split is what makes the store-switch remount reset pagination: the
 * offset lives in OrderHistoryPageInner's own useState, which key={storeId}
 * remounts (see OrderHistoryPage.tsx).
 */
import {trpc} from '../../trpc'
import {toHistoryRows} from './orderHistoryViewModel'
import type {OrderHistoryRow} from './orderHistoryViewModel'

type UseOrderHistoryInput = {
  storeId: number
  limit: number
  offset: number
}

export type OrderHistoryViewModel = {
  rows: Array<OrderHistoryRow>
  total: number
  limit: number
  offset: number
  isLoading: boolean
  isError: boolean
  error: unknown
}

export const useOrderHistory = ({storeId, limit, offset}: UseOrderHistoryInput): OrderHistoryViewModel => {
  const query = trpc.orders.listByStore.useQuery({storeId, limit, offset})

  const total = query.data?.total ?? 0
  const items = query.data?.items ?? []

  return {
    rows: toHistoryRows(items),
    total,
    limit,
    offset,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  }
}
